import React, { useState, useEffect } from "react";
import PercentIcon from "@mui/icons-material/Percent";
import { Helmet } from "react-helmet";
import LoanPDFGenerator from "../pdf/pdf-loans-comparison";
import { saveLoan, updateLoanTitle } from "../Database/loan-comparisonDB";
import { LoanData } from "../models/loan-comparison";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { Typography, IconButton } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { useLocation } from "react-router-dom";
import { setCurrencySymbol } from "../Redux/currencySlice";
import ReactGA from 'react-ga4'
import SavedSnackbar from "../utils/saved-snackbar";

const LoanComparison: React.FC<{ loan?: LoanData }> = ({ loan }) => {
  const MAX_PERIOD = 30;
  const location = useLocation();
  const [loanData, setLoanData] = useState<LoanData | null>(null);

  const [loan1Amount, setLoan1Amount] = useState("1,00,000");
  const [loan1Rate, setLoan1Rate] = useState("7.50");
  const [loan1Period, setLoan1Period] = useState("5");
  const [loan2Amount, setLoan2Amount] = useState("1,50,000");
  const [loan2Rate, setLoan2Rate] = useState("6.75");
  const [loan2Period, setLoan2Period] = useState("5");
  const [isLoan1InMonths, setIsLoan1InMonths] = useState(false);
  const [isLoan2InMonths, setIsLoan2InMonths] = useState(false);

  const [loan1FinalAmount, setLoan1FinalAmount] = useState<number>(0);
  const [loan2FinalAmount, setLoan2FinalAmount] = useState<number>(0);
  const [loan1TotalInterest, setLoan1TotalInterest] = useState<number>(0);
  const [loan2TotalInterest, setLoan2TotalInterest] = useState<number>(0);
  const [loan1EMI, setLoan1EMI] = useState<number>(0);
  const [loan2EMI, setLoan2EMI] = useState<number>(0);
  const [showResults, setShowResults] = useState(true);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);

  const [errors, setErrors] = useState({
    loan1Amount: "",
    loan1Rate: "",
    loan1Period: "",
    loan2Amount: "",
    loan2Rate: "",
    loan2Period: "",
  });

  const formatNumberWithCommas = (value: number | string) => {
    return new Intl.NumberFormat("en-IN").format(Number(value));
  };

  const calculateEMI = (amount: number, rate: number, period: number) => {
    const monthlyRate = rate / (12 * 100);
    const totalPayments = period * 12;

    if (monthlyRate === 0) return amount / totalPayments;

    return (
      (amount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
      (Math.pow(1 + monthlyRate, totalPayments) - 1)
    );
  };

  const validateInputs = () => {
    const newErrors = {
      loan1Amount: "",
      loan1Rate: "",
      loan1Period: "",
      loan2Amount: "",
      loan2Rate: "",
      loan2Period: "",
    };
    let isValid = true;

    const minLoanAmount = 1000;

    // Loan 1 Validation
    if (!loan1Amount) {
      newErrors.loan1Amount = "Loan amount is mandatory.";
      isValid = false;
    } else if (parseFloat(loan1Amount.replace(/,/g, "")) < minLoanAmount) {
      newErrors.loan1Amount = "Loan amount can't be less than " + currencySymbol + "1000.";
      isValid = false;
    }

    if (!loan1Rate) {
      newErrors.loan1Rate = "Interest rate is mandatory.";
      isValid = false;
    }

    if (!loan1Period) {
      newErrors.loan1Period = "Years are mandatory.";
      isValid = false;
    }

    // Loan 2 Validation
    if (!loan2Amount) {
      newErrors.loan2Amount = "Loan amount is mandatory.";
      isValid = false;
    } else if (parseFloat(loan2Amount.replace(/,/g, "")) < minLoanAmount) {
      newErrors.loan2Amount = "Loan amount can't be less than " + currencySymbol + "1000.";
      isValid = false;
    }

    if (!loan2Rate) {
      newErrors.loan2Rate = "Interest rate is mandatory.";
      isValid = false;
    }

    if (!loan2Period) {
      newErrors.loan2Period = "Years are mandatory.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };


  const clearError = (field: keyof typeof errors) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
  };

  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }
    const updatedData: LoanData = {
      title: title,
      loan1Amount: parseFloat(loan1Amount.replace(/,/g, "")),
      loan1Rate: parseFloat(loan1Rate),
      loan1Period: parseInt(loan1Period),
      loan2Amount: parseFloat(loan2Amount.replace(/,/g, "")),
      loan2Rate: parseFloat(loan2Rate),
      loan2Period: parseInt(loan2Period),
      currencySymbol: currencySymbol,
      loan1PeriodUnit: isLoan1InMonths ? 'months' : 'years',
      loan2PeriodUnit: isLoan2InMonths ? 'months' : 'years',
    };

    if (loanData?.id) {
      await updateLoanTitle(loanData.id, updatedData);
      console.log(`Loan with ID ${loanData.id} updated successfully.`);
    } else {
      await saveLoan(updatedData);
      console.log("New loan created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };

  useEffect(() => {
    const { loan: passedLoan, currencySymbol: passedCurrencySymbol } =
      location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (passedLoan) {
      setLoanData(passedLoan);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setLoan1Amount(passedLoan.loan1Amount.toString());
      setLoan1Rate(passedLoan.loan1Rate.toString());
      setLoan1Period(passedLoan.loan1Period.toString());
      setLoan2Amount(passedLoan.loan2Amount.toString());
      setLoan2Rate(passedLoan.loan2Rate.toString());
      setLoan2Period(passedLoan.loan2Period.toString());
      setIsLoan1InMonths(passedLoan.loan1PeriodUnit === 'months');
      setIsLoan2InMonths(passedLoan.loan2PeriodUnit === 'months');
      setTitle(passedLoan.title);
      setSavedTitle(passedLoan.title || "");


    }
    handleCalculate();

  }, [location.state, , loanData]);

  const handleCalculate = () => {
    ReactGA.event({
      category: 'Loan Comparison Calculator',
      action: 'Loan Comparison Calculate Button',
      label: 'Loan Comparison Calculator'
    });

    if (!validateInputs()) return;

    const calculateFinalAmountAndInterest = (
      amount: string,
      rate: string,
      period: string,
      isMonths: boolean
    ) => {
      const principal = parseFloat(amount.replace(/,/g, ""));
      const interestRate = parseFloat(rate);
      let years = parseFloat(period);

      if (isMonths) {
        years = years / 12;
      }

      if (isNaN(principal) || isNaN(interestRate) || isNaN(years)) {
        return { finalAmount: null, totalInterest: null, emi: null };
      }

      const emi = calculateEMI(principal, interestRate, years);
      const totalPayments = emi * years * 12;
      const totalInterest = totalPayments - principal;
      const finalAmount = totalPayments;

      return { finalAmount, totalInterest, emi };
    };

    const {
      finalAmount: finalAmount1,
      totalInterest: totalInterest1,
      emi: emi1,
    } = calculateFinalAmountAndInterest(loan1Amount, loan1Rate, loan1Period, isLoan1InMonths);

    const {
      finalAmount: finalAmount2,
      totalInterest: totalInterest2,
      emi: emi2,
    } = calculateFinalAmountAndInterest(loan2Amount, loan2Rate, loan2Period, isLoan2InMonths);

    setLoan1TotalInterest(totalInterest1 ?? 0);
    setLoan2TotalInterest(totalInterest2 ?? 0);
    setLoan1FinalAmount(finalAmount1 ?? 0);
    setLoan2FinalAmount(finalAmount2 ?? 0);
    setLoan1EMI(emi1 ?? 0);
    setLoan2EMI(emi2 ?? 0);
    setShowResults(true);
    setShowPdfButton(true);
    setIsCalculated(true)
  };


  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    field: keyof typeof errors
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      clearError(field);
    };
  };

  const handleLoanAmountChange = (
    setAmount: React.Dispatch<React.SetStateAction<string>>,
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof errors
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = parseFloat(value);

    if (value === "" || numericValue <= 10000000) {
      setAmount(value ? formatNumberWithCommas(numericValue) : "");
      clearError(field);
    }
  };

  const handleLoan1AmountChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleLoanAmountChange(setLoan1Amount, e, "loan1Amount");
  const handleLoan1RateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const numericValue = parseFloat(value);
    if (value === "" || (numericValue >= 0 && numericValue <= 100)) {
      if (!isNaN(numericValue)) {
        value = numericValue.toFixed(2);
      }
      setLoan1Rate(value);
    }
  };
  const handleLoan1PeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = parseFloat(value);
    if (value === "" || (numericValue >= 0 && numericValue <= 100)) {
      setLoan1Period(value);
    }
  };
  const handleLoan2AmountChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleLoanAmountChange(setLoan2Amount, e, "loan2Amount");
  const handleLoan2RateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const numericValue = parseFloat(value);
    if (value === "" || (numericValue >= 0 && numericValue <= 100)) {
      if (!isNaN(numericValue)) {
        value = numericValue.toFixed(2);
      }
      setLoan2Rate(value);
    }
  };
  const handleLoan2PeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = parseFloat(value);
    if (value === "" || (numericValue >= 0 && numericValue <= 100)) {
      setLoan2Period(value);
    }
  };
  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLimit: number,
    decimalLimit: number = 2,
    minLimit: number = 1
  ) => {
    const value = e.target.value;

    if (value === "") {
      setter("");
      return;
    }

    const validValue = value.replace(/[^0-9.]/g, "");
    const parts = validValue.split(".");

    if (parts.length > 1 && parts[1].length > decimalLimit) {
      parts[1] = parts[1].slice(0, decimalLimit);
    }

    const formattedValue = parts.join(".");

    const numericValue = parseFloat(formattedValue);
    if ((numericValue >= minLimit && numericValue <= maxLimit) || formattedValue === "") {
      setter(formattedValue);
    }
  };



  const handlePeriod1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maxValue = isLoan1InMonths ? 120 : 30;

    if (value === "" || /^\d*$/.test(value)) {

      const numberValue = value === "" ? "" : parseInt(value, 10);
      if (numberValue === "" || (numberValue >= 1 && numberValue <= maxValue)) {
        setLoan1Period(value);
        setErrors(prevErrors => ({
          ...prevErrors,
          loan1Period: ""
        }));
      } else {
        setErrors(prevErrors => ({
          ...prevErrors,
          loan1Period: `Please enter a value between 1 and ${maxValue}.`
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        loan1Period: "Please enter a valid number."
      }));
    }
  };


  const handlePeriod2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maxValue = isLoan2InMonths ? 120 : 30;  
  
    if (value === "" || /^\d*$/.test(value)) {
      const numberValue = value === "" ? "" : parseInt(value, 10);
      if (numberValue === "" || (numberValue >= 1 && numberValue <= maxValue)) {
        setLoan2Period(value);
        setErrors(prevErrors => ({
          ...prevErrors,
          loan2Period: ""
        }));
      } else {
        setErrors(prevErrors => ({
          ...prevErrors,
          loan2Period: `Please enter a value between 1 and ${maxValue}.`
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        loan2Period: "Please enter a valid number."
      }));
    }
  };
  

  const handleReset = () => {
    ReactGA.event({ 'category': 'Loan Comparision calculator', 'action': 'Loan Comparision Reset Button', 'label': 'Loan Comparision Reset' })
    setLoan1Amount("1,00,000");
    setLoan1Rate("7.50");
    setLoan1Period("5");

    setLoan2Amount("1,50,000");
    setLoan2Rate("6.75");
    setLoan2Period("5");

    setLoan1EMI(0);
    setLoan2EMI(0);
    setLoan1TotalInterest(0);
    setLoan2TotalInterest(0);
    setLoan1FinalAmount(0);
    setLoan2FinalAmount(0);
    setShowResults(false);
    setShowPdfButton(false);

    setErrors({
      loan1Amount: "",
      loan1Rate: "",
      loan1Period: "",
      loan2Amount: "",
      loan2Rate: "",
      loan2Period: "",
    });

  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };


  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Loan Comparison Calculator </title>
        <meta
          name="description"
          content="Compare different loan options with our Loan Comparison Calculator. Analyze interest rates, EMIs, and total payments to make an informed decision on the best loan for your needs."
        />
        <link rel="canonical" href="/loan-comparision-calculator.html" />
      </Helmet>

      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      <div className="container mt-3" onKeyPress={handleKeyPress} >
        <div className="col-12 col-md-10 mx-auto">
          <div className="col-12 mb-3">
            {savedTitle && (
              <Typography variant="h6" style={{ marginLeft: '20px' }}>
                {savedTitle}
              </Typography>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
              Compare Loans
            </h5>
          </div>
          <p>A Loan Comparison Calculator helps users compare loan options based on amount, interest rate, and period.</p>
          <div className="mb-4">
            <div className="card mb-4">
              <h5 className="card-header">Loan 1</h5>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label htmlFor="loan1Amount" className="form-label">
                      Loan Amount ({currencySymbol})
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.loan1Amount ? "is-invalid" : ""
                        }`}
                      id="loan1Amount"
                      value={loan1Amount}
                      onChange={handleLoan1AmountChange}
                    />
                    {errors.loan1Amount && (
                      <div className="invalid-feedback">
                        {errors.loan1Amount}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="loan1Rate" className="form-label">
                      Interest Rate (%)
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${errors.loan1Rate ? "is-invalid" : ""
                          }`}
                        id="loan1Rate"
                        value={loan1Rate}
                        onChange={(e) =>
                          handleNumberInput(e, setLoan1Rate, 30, 2, 1)
                        }

                      />
                      <span className="input-group-text">
                        <PercentIcon />
                      </span>
                      {errors.loan1Rate && (
                        <div className="invalid-feedback">
                          {errors.loan1Rate}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="loan1Period" className="form-label">Loan Period</label>
                    <div className="input-group">
                      <input
                        type="text" // It's better to use "number" type for numeric input
                        className={`form-control ${errors.loan1Period ? "is-invalid" : ""}`}
                        id="loan1Period"
                        value={loan1Period}
                        onChange={handlePeriod1Change}
                        max={isLoan1InMonths ? 120 : 30} // Max value based on months or years
                      />
                      <button
                        type="button"
                        className={`btn ${isLoan1InMonths ? 'btn-outline-secondary' : 'btn-primary'} no-border-radius`}
                        onClick={() => setIsLoan1InMonths(false)}
                      >
                        Years
                      </button>
                      <button
                        type="button"
                        className={`btn ${isLoan1InMonths ? 'btn-primary' : 'btn-outline-secondary'} no-border-radius`}
                        onClick={() => setIsLoan1InMonths(true)}
                      >
                        Months
                      </button>
                    </div>

                    {/* Error message if value is invalid */}
                    {errors.loan1Period && (
                      <div className="invalid-feedback">
                        {errors.loan1Period}
                      </div>
                    )}
                  </div>


                </div>
              </div>
            </div>
            <div className="card mb-4">
              <h5 className="card-header">Loan 2</h5>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label htmlFor="loan2Amount" className="form-label">
                      Loan Amount ({currencySymbol})
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.loan2Amount ? "is-invalid" : ""
                        }`}
                      id="loan2Amount"
                      value={loan2Amount}
                      onChange={handleLoan2AmountChange}
                    />
                    {errors.loan2Amount && (
                      <div className="invalid-feedback">
                        {errors.loan2Amount}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="loan2Rate" className="form-label">
                      Interest Rate (%)
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${errors.loan2Rate ? "is-invalid" : ""
                          }`}
                        id="loan2Rate"
                        value={loan2Rate}
                        onChange={(e) =>
                          handleNumberInput(e, setLoan2Rate, 30, 2, 1)
                        }
                      />
                      <span className="input-group-text">
                        <PercentIcon />
                      </span>
                      {errors.loan2Rate && (
                        <div className="invalid-feedback">
                          {errors.loan2Rate}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="loan2Period" className="form-label">Loan Period</label>
                    <div className="input-group">
                      <input
                        type="text" 
                        className={`form-control ${errors.loan2Period ? "is-invalid" : ""}`}
                        id="loan2Period"
                        value={loan2Period}
                        onChange={handlePeriod2Change}
                        max={isLoan2InMonths ? 120 : 30} 
                      />
                      <button
                        type="button"
                        className={`btn ${isLoan2InMonths ? 'btn-outline-secondary' : 'btn-primary'} no-border-radius`}
                        onClick={() => setIsLoan2InMonths(false)}
                      >
                        Years
                      </button>
                      <button
                        type="button"
                        className={`btn ${isLoan2InMonths ? 'btn-primary' : 'btn-outline-secondary'} no-border-radius`}
                        onClick={() => setIsLoan2InMonths(true)}
                      >
                        Months
                      </button>
                    </div>

                    {/* Error message for loan2Period */}
                    {errors.loan2Period && (
                      <div className="invalid-feedback">
                        {errors.loan2Period}
                      </div>
                    )}
                  </div>


                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center mb-2">

            <button className="btn btn-primary" onClick={handleCalculate}>
              Compare Loans
            </button>

          </div>
          <div className='d-flex justify-content-center'>
            <a
              onClick={handleReset}
              style={{ cursor: "pointer", textDecoration: "none" }}
              className="mb-2"
            >
              Reset
            </a>
          </div>

          {showResults && (
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title ">Overall Results</h5>
                  <IconButton color="secondary" onClick={handleDialogOpen}>
                    {icon}
                  </IconButton>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="text-center">
                    <h6>Monthly EMI</h6>
                    <div className="d-flex justify-content-between w-50">
                      <span
                        style={{
                          color: loan1EMI < loan2EMI ? "green" : loan1EMI > loan2EMI ? "red" : "black",
                          marginRight: "15px"
                        }}
                      >
                        {loan1EMI !== null ? `${formatNumberWithCommas(loan1EMI.toFixed(2))}` : ""}
                      </span>
                      <span>|</span>
                      <span
                        style={{
                          color: loan2EMI < loan1EMI ? "green" : loan2EMI > loan1EMI ? "red" : "black",
                          marginLeft: "15px"
                        }}
                      >
                        {loan2EMI !== null ? `${formatNumberWithCommas(loan2EMI.toFixed(2))}` : ""}
                      </span>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <h6>Total Interest</h6>
                    <div className="d-flex justify-content-between w-50">
                      <span
                        style={{
                          color: loan1TotalInterest < loan2TotalInterest ? "green" : loan1TotalInterest > loan2TotalInterest ? "red" : "black",
                          marginRight: "15px"
                        }}
                      >
                        {loan1TotalInterest !== null ? `${formatNumberWithCommas(loan1TotalInterest.toFixed(2))}` : ""}
                      </span>
                      <span>|</span>
                      <span
                        style={{
                          color: loan2TotalInterest < loan1TotalInterest ? "green" : loan2TotalInterest > loan1TotalInterest ? "red" : "black",
                          marginLeft: "15px"
                        }}
                      >
                        {loan2TotalInterest !== null ? `${formatNumberWithCommas(loan2TotalInterest.toFixed(2))}` : ""}
                      </span>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <h6>Total Amount</h6>
                    <div className="d-flex justify-content-between w-50">
                      <span
                        style={{
                          color: loan1FinalAmount < loan2FinalAmount ? "green" : loan1FinalAmount > loan2FinalAmount ? "red" : "black",
                          marginRight: "15px"
                        }}
                      >
                        {loan1FinalAmount !== null ? `${formatNumberWithCommas(loan1FinalAmount.toFixed(2))}` : ""}
                      </span>
                      <span>|</span>
                      <span
                        style={{
                          color: loan2FinalAmount < loan1FinalAmount ? "green" : loan2FinalAmount > loan1FinalAmount ? "red" : "black",
                          marginLeft: "15px"
                        }}
                      >
                        {loan2FinalAmount !== null ? `${formatNumberWithCommas(loan2FinalAmount.toFixed(2))}` : ""}
                      </span>
                    </div>
                  </div>
                </div>



                {loan1FinalAmount !== null && loan2FinalAmount !== null && (
                  <h5 className="text-center mt-3">
                    Difference in Final Amount:
                    <span style={{ color: "red" }}>
                      ({currencySymbol})
                      {formatNumberWithCommas(Math.abs(loan1FinalAmount - loan2FinalAmount).toFixed(2))}
                    </span>
                  </h5>
                )}

              </div>
            </div>
          )}
          <div className="d-flex justify-content-center" style={{ marginTop: "70px", paddingLeft: "90px" }}>
            {showPdfButton && (
              <LoanPDFGenerator
                loan1EMI={loan1EMI}
                loan1TotalInterest={loan1TotalInterest}
                loan1FinalAmount={loan1FinalAmount}
                loan2EMI={loan2EMI}
                loan2TotalInterest={loan2TotalInterest}
                loan2FinalAmount={loan2FinalAmount}
                loan1Amount={loan1Amount}
                loan1Rate={loan1Rate}
                loan1Period={loan1Period}
                loan2Amount={loan2Amount}
                loan2Rate={loan2Rate}
                loan2Period={loan2Period}
                formatNumberWithCommas={formatNumberWithCommas}
                isLoan1InMonths={isLoan1InMonths}
                isLoan2InMonths={isLoan2InMonths}
                isCalculated={isCalculated}
              />
            )}
          </div>

        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}

        />
      </div>
    </>
  );
};

export default LoanComparison;
